import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Modal, Typography, Card, Tag, Space, Row, Col, Timeline, Divider, Empty, Button, Form, Select, Input, message, Spin } from 'antd';
import { useTranslation } from 'react-i18next';
import { 
  VideoCameraOutlined, 
  GlobalOutlined, 
  HomeOutlined, 
  QuestionOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  UserOutlined,
  BookOutlined,
  HistoryOutlined,
  EditOutlined,
  SwapOutlined,
  SaveOutlined,
  CloseCircleOutlined
} from '@ant-design/icons';
import moment from 'moment-timezone';

const { Title, Text } = Typography;
const { Option } = Select;

// Start times 08:00-23:00 in 15 minute steps, stored as HH:mm:ss
const timeOptions = Array.from({ length: (23 - 8 + 1) * 4 }, (_, index) => {
  const hour = 8 + Math.floor(index / 4);
  const minutes = (index % 4) * 15;
  const label = `${hour.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  return { label, value: `${label}:00` };
});

// Durations 15 minutes to 3 hours, valued in minutes (how the column is stored)
const hourOptions = Array.from({ length: 12 }, (_, index) => {
  const minutes = (index + 1) * 15;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return { label: `${h}h ${m.toString().padStart(2, '0')}m`, value: minutes };
});

// calendar-classes returns schedule_hour pre-formatted as "2h 30m"; the update
// endpoint expects plain minutes, so convert back
const durationToMinutes = (value) => {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'number') return value;

  const text = String(value).trim();
  const hm = text.match(/^(\d+)\s*h\s*(\d+)\s*m$/i);
  if (hm) return parseInt(hm[1], 10) * 60 + parseInt(hm[2], 10);

  const parsed = parseInt(text, 10);
  return Number.isNaN(parsed) ? null : parsed;
};

const ClassDetailsModal = ({ visible, onClose, classData, teacherData, classChangeLogs, onRefresh }) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const API_URL = process.env.REACT_APP_API_URL;

  // Local copy so a saved change shows straight away, before the parent refetches
  const [details, setDetails] = useState(classData);
  const [teachers, setTeachers] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setDetails(classData);
    setEditingField(null);
  }, [classData]);

  const fetchAllData = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [teachersRes, subjectsRes] = await Promise.all([
        axios.get(`${API_URL}/teachers`, { headers }),
        axios.get(`${API_URL}/subjects`, { headers }),
      ]);

      setTeachers((teachersRes.data?.teachers || []).filter((teacher) => teacher.status === 'active'));
      setSubjects(subjectsRes.data?.subjects || subjectsRes.data || []);
    } catch (error) {
      console.error('Error fetching class edit options:', error);
      message.error(t('Failed to load options'));
    } finally {
      setLoading(false);
    }
  }, [API_URL, t]);

  useEffect(() => {
    if (visible) fetchAllData();
  }, [visible, fetchAllData]);

  // Get teacher name
  const getTeacherName = () => {
    const editedTeacher = teachers.find((teacher) => teacher.userid === details?.teacher_id);
    const source = editedTeacher || teacherData;
    if (!source) return '-';
    return `${source.nickname || ''} ${source.firstname || ''} ${source.lastname || ''}`.trim() || '-';
  };

  // Get status tag
  const getStatusTag = (status) => {
    if (!status) return <Tag color="default">No Status</Tag>;
    
    switch(status.toLowerCase()) {
      case 'approved':
        return <Tag color="green">Approved</Tag>;
      case 'pending':
        return <Tag color="orange">Pending</Tag>;
      case 'cancelled':
        return <Tag color="red">Cancelled</Tag>;
      default:
        return <Tag color="default">{status}</Tag>;
    }
  };

  const formatPlatform = (platform) => {
    switch (platform) {
      case 'online':
        return (
          <Space>
            <VideoCameraOutlined style={{ color: '#1890ff' }} /> {t('online')}
          </Space>
        );
      case 'onsite':
        return (
          <Space>
            <GlobalOutlined style={{ color: '#52c41a' }} /> {t('onsite')}
          </Space>
        );
      case 'athome':
        return (
          <Space>
            <HomeOutlined style={{ color: '#fa8c16' }} /> {t('athome')}
          </Space>
        );
      default:
        return (
          <Space>
            <QuestionOutlined style={{ color: '#d9d9d9' }} /> -
          </Space>
        );
    }
  };

  // Format date only
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    
    try {
      const date = moment(dateString);
      if (date.isValid()) {
        return date.format('DD MMM YY');
      }
      return '-';
    } catch (error) {
      console.error('Error formatting date:', error);
      return '-';
    }
  };

  // Format time only
  const formatTime = (timeString) => {
    if (!timeString) return '-';
    
    try {
      const time = moment(timeString, 'HH:mm:ss');
      if (time.isValid()) {
        return time.format('HH:mm');
      }
      return timeString;
    } catch (error) {
      console.error('Error formatting time:', error);
      return timeString;
    }
  };

  // Format duration to "2:30hrs" format
  const formatDuration = (durationString) => {
    if (!durationString) return '-';
    
    try {
      // Handle format like "2h30m" or "2 30"
      if (durationString.includes('h') && durationString.includes('m')) {
        const hours = parseInt(durationString.split('h')[0]);
        const minutes = parseInt(durationString.split('h')[1].replace('m', ''));
        return `${hours}:${minutes.toString().padStart(2, '0')} hr(s)`;
      }
      
      // Handle format like "2 30" (hours and minutes with space)
      if (durationString.includes(' ')) {
        const [hours, minutes] = durationString.split(' ').map(part => parseInt(part));
        return `${hours}:${minutes.toString().padStart(2, '0')} hr(s)`;
      }
      
      // Try to convert minutes to hours:minutes format
      const totalMinutes = parseInt(durationString);
      if (!isNaN(totalMinutes)) {
        const hours = Math.floor(totalMinutes / 60);
        const minutes = totalMinutes % 60;
        return `${hours}:${minutes.toString().padStart(2, '0')} hr(s)`;
      }
      
      // If we can't parse it, return as is
      return durationString;
    } catch (error) {
      console.error('Error formatting duration:', error);
      return durationString || '-';
    }
  };

  // Format change log timestamp
  const formatLogTimestamp = (timestamp) => {
    if (!timestamp) return '-';
    try {
      return moment(timestamp).format('DD MMM YY HH:mm');
    } catch (error) {
      return timestamp;
    }
  };

  // Filter class change logs for current class
  const getFilteredClassChangeLogs = () => {
    if (!classChangeLogs || !details?.classid) return [];
    return classChangeLogs.filter(log => log.classid === details.classid);
  };

  // Get change type icon
  const getChangeIcon = (changeType) => {
    switch(changeType?.toLowerCase()) {
      case 'reschedule':
      case 'time_change':
        return <ClockCircleOutlined style={{ color: '#fa8c16' }} />;
      case 'teacher_change':
        return <UserOutlined style={{ color: '#1890ff' }} />;
      case 'platform_change':
        return <SwapOutlined style={{ color: '#52c41a' }} />;
      case 'status_change':
        return <EditOutlined style={{ color: '#722ed1' }} />;
      default:
        return <EditOutlined style={{ color: '#8c8c8c' }} />;
    }
  };

  // Get change description based on the log data
  const getChangeDescription = (log) => {
    if (log.changereason) return log.changereason;
    if (log.note) return log.note;
    
    // Generate description based on what changed
    const changes = [];
    if (log.originaldate !== log.newdate) {
      changes.push(`Date changed from ${formatDate(log.originaldate)} to ${formatDate(log.newdate)}`);
    }
    if (log.originalstarttime !== log.newstarttime) {
      changes.push(`Start time changed from ${formatTime(log.originalstarttime)} to ${formatTime(log.newstarttime)}`);
    }
    if (log.originalendtime !== log.newendtime) {
      changes.push(`End time changed from ${formatTime(log.originalendtime)} to ${formatTime(log.newendtime)}`);
    }
    
    return changes.length > 0 ? changes.join(', ') : 'Class details updated';
  };

  // Open one field for editing and seed the form with its current value
  const handleEditClick = (fieldName) => {
    setEditingField(fieldName);

    switch (fieldName) {
      case 'teacher':
        form.setFieldsValue({ teacher_id: details?.teacher_id });
        break;
      case 'subject':
        form.setFieldsValue({ subject_id: details?.subject_id });
        break;
      case 'date':
        form.setFieldsValue({ date: details?.date ? moment(details.date).format('YYYY-MM-DD') : undefined });
        break;
      case 'time':
        form.setFieldsValue({ schedule_time: details?.schedule_time });
        break;
      case 'duration':
        form.setFieldsValue({ duration_minutes: durationToMinutes(details?.schedule_hour) || 60 });
        break;
      case 'platform':
        form.setFieldsValue({ platform: details?.platform });
        break;
      default:
        break;
    }
  };

  const handleCancelEdit = () => {
    setEditingField(null);
    form.resetFields();
  };

  // Save the field being edited. PUT /classes/:id handles one field group per
  // call, which is exactly one card at a time.
  const handleSaveField = async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      const values = await form.validateFields();

      let updateData = {};
      let localChange = {};

      switch (editingField) {
        case 'teacher': {
          updateData = { teacher_id: values.teacher_id };
          localChange = { teacher_id: values.teacher_id };
          break;
        }
        case 'subject': {
          const subject = subjects.find((item) => item.subjectid === values.subject_id);
          updateData = { subjectid: values.subject_id };
          localChange = { subject_id: values.subject_id, subject_name: subject?.title };
          break;
        }
        case 'date': {
          updateData = { date: values.date };
          localChange = { date: values.date };
          break;
        }
        case 'time': {
          updateData = { schedule_time: values.schedule_time };
          localChange = { schedule_time: values.schedule_time };
          break;
        }
        case 'duration': {
          const minutes = values.duration_minutes;
          updateData = { schedule_hour: minutes };
          localChange = { schedule_hour: `${Math.floor(minutes / 60)}h ${minutes % 60}m` };
          break;
        }
        case 'platform': {
          updateData = { platform: values.platform };
          localChange = { platform: values.platform };
          break;
        }
        default:
          break;
      }

      await axios.put(`${API_URL}/classes/${details.classid}`, updateData, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setDetails((prev) => ({ ...prev, ...localChange }));
      message.success(t('Class updated successfully'));
      setEditingField(null);
      form.resetFields();

      if (onRefresh) await onRefresh();

      // Close once saved; the calendar behind it already shows the change
      if (onClose) onClose();
    } catch (error) {
      if (error?.errorFields) return; // form validation, already shown inline
      console.error('Error updating class:', error);
      message.error(error?.response?.data?.message || t('Failed to update class'));
    } finally {
      setSaving(false);
    }
  };

  // Render the editor for the field currently open
  const renderEditForm = (fieldName) => {
    switch (fieldName) {
      case 'teacher':
        return (
          <Form.Item name="teacher_id" style={{ marginBottom: 0 }}>
            <Select
              showSearch
              placeholder={t('Select Teacher')}
              optionFilterProp="label"
              options={teachers.map((teacher) => ({
                value: teacher.userid,
                label: `${teacher.nickname || ''} ${teacher.firstname || ''} ${teacher.lastname || ''}`.trim(),
              }))}
            />
          </Form.Item>
        );

      case 'subject':
        return (
          <Form.Item name="subject_id" style={{ marginBottom: 0 }}>
            <Select
              showSearch
              placeholder={t('Select Subject')}
              optionFilterProp="label"
              options={subjects.map((subject) => ({
                value: subject.subjectid,
                label: subject.title,
              }))}
            />
          </Form.Item>
        );

      case 'date':
        return (
          <Form.Item name="date" style={{ marginBottom: 0 }}>
            <Input type="date" style={{ width: '100%' }} />
          </Form.Item>
        );

      case 'time':
        return (
          <Form.Item name="schedule_time" style={{ marginBottom: 0 }}>
            <Select showSearch placeholder={t('Select Time')} style={{ width: '100%' }} options={timeOptions} />
          </Form.Item>
        );

      case 'duration':
        return (
          <Form.Item name="duration_minutes" style={{ marginBottom: 0 }}>
            <Select placeholder={t('Select Duration')} style={{ width: '100%' }} options={hourOptions} />
          </Form.Item>
        );

      case 'platform':
        return (
          <Form.Item name="platform" style={{ marginBottom: 0 }}>
            <Select style={{ width: '100%' }}>
              <Option value="online"><Space><VideoCameraOutlined /> {t('online')}</Space></Option>
              <Option value="onsite"><Space><GlobalOutlined /> {t('onsite')}</Space></Option>
              <Option value="athome"><Space><HomeOutlined /> {t('athome')}</Space></Option>
            </Select>
          </Form.Item>
        );

      default:
        return null;
    }
  };

  const InfoCard = ({ icon, title, value, color = '#1890ff', fieldName, editable = false }) => (
    <Card 
      size="small" 
      style={{ 
        height: '100%',
        borderLeft: `4px solid ${color}`,
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
      }}
    >
      <Space direction="vertical" size={4} style={{ width: '100%' }}>
        <Space style={{ justifyContent: 'space-between', width: '100%' }}>
          <Space>
            {React.cloneElement(icon, { style: { color, fontSize: '16px' } })}
            <Text strong style={{ color: '#595959' }}>{title}</Text>
          </Space>
          {editable && editingField !== fieldName && (
            <Button
              type="text"
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleEditClick(fieldName)}
              title={t('edit')}
            />
          )}
        </Space>

        {editingField === fieldName ? (
          <div style={{ width: '100%' }}>
            {renderEditForm(fieldName)}
            <Space style={{ marginTop: 8 }}>
              <Button
                type="primary"
                size="small"
                icon={<SaveOutlined />}
                onClick={handleSaveField}
                loading={saving}
              >
                {t('Save')}
              </Button>
              <Button size="small" icon={<CloseCircleOutlined />} onClick={handleCancelEdit}>
                {t('Cancel')}
              </Button>
            </Space>
          </div>
        ) : (
          <div style={{ fontSize: '16px', fontWeight: '500', color: '#262626' }}>
            {value}
          </div>
        )}
      </Space>
    </Card>
  );

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      width={900}
      footer={null}
      centered
      style={{ top: 20 }}
    >
      {details ? (
        <Spin spinning={loading}>
        <Form form={form} component={false}>
        <div>
          {/* Header */}
          <div style={{ textAlign: 'center', marginBottom: '24px' }}>
            <Title level={2} style={{ marginBottom: '8px', color: '#1890ff' }}>
              {details.subject_name}
            </Title>
            <Space size="large">
              {getStatusTag(details.status)}
              <Text type="secondary" style={{ fontSize: '14px' }}>
                Class ID: {details.classid}
              </Text>
              <Text type="secondary" style={{ fontSize: '14px' }}>
                Code: {details.class_code || '-'}
              </Text>
            </Space>
          </div>

          {/* Class Details Cards */}
          <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
            <Col xs={24} sm={12} md={8}>
              <InfoCard
                icon={<UserOutlined />}
                title="TEACHER"
                value={getTeacherName()}
                color="#722ed1"
                fieldName="teacher"
                editable
              />
            </Col>
            <Col xs={24} sm={12} md={8}>
              <InfoCard
                icon={<BookOutlined />}
                title="SUBJECT"
                value={details.subject_name || '-'}
                color="#eb2f96"
                fieldName="subject"
                editable
              />
            </Col>
            <Col xs={24} sm={12} md={8}>
              <InfoCard
                icon={<CalendarOutlined />}
                title="DATE"
                value={formatDate(details.date)}
                color="#52c41a"
                fieldName="date"
                editable
              />
            </Col>
            <Col xs={24} sm={12} md={8}>
              <InfoCard
                icon={<ClockCircleOutlined />}
                title="TIME"
                value={`${formatTime(details.schedule_time)} - ${formatTime(details.end_time)}`}
                color="#fa8c16"
                fieldName="time"
                editable
              />
            </Col>
            <Col xs={24} sm={12} md={8}>
              <InfoCard
                icon={<BookOutlined />}
                title="DURATION"
                value={formatDuration(details.schedule_hour || details.duration)}
                color="#13c2c2"
                fieldName="duration"
                editable
              />
            </Col>
            <Col xs={24} sm={12} md={8}>
              <InfoCard
                icon={<VideoCameraOutlined />}
                title="PLATFORM"
                value={formatPlatform(details.platform)}
                color="#1890ff"
                fieldName="platform"
                editable
              />
            </Col>
          </Row>
          {details.book_details_materials && (
            <Card
              size="small"
              style={{
                marginBottom: '24px',
                backgroundColor: '#fffbe6',
                borderLeft: '4px solid #faad14',
              }}
            >
              <Space direction="vertical" size="small">
                <Text strong style={{ fontSize: '14px' }}>
                  <EditOutlined style={{ marginRight: 8 }} />
                  Note
                </Text>
                <Text style={{ whiteSpace: 'pre-line' }}>{details.book_details_materials}</Text>
              </Space>
            </Card>
          )}


          {/* Change Logs Section */}
          <Divider orientation="left">
            <Space>
              <HistoryOutlined style={{ color: '#1890ff' }} />
              <Text strong>Class Change History</Text>
            </Space>
          </Divider>

          <Card 
            style={{ 
              maxHeight: '300px', 
              overflowY: 'auto',
              backgroundColor: '#fafafa'
            }}
          >
            {(() => {
              const filteredLogs = getFilteredClassChangeLogs();
              return filteredLogs && filteredLogs.length > 0 ? (
                <Timeline
                  items={filteredLogs.map((log, index) => ({
                    dot: getChangeIcon('reschedule'), // You can enhance this based on log type
                    children: (
                      <div key={index}>
                        <div style={{ marginBottom: '4px' }}>
                          <Text strong style={{ color: '#262626' }}>
                            {log.note}
                          </Text>
                          <Text type="secondary" style={{ marginLeft: '8px', fontSize: '12px' }}>
                            {formatLogTimestamp(log.updated_at)}
                          </Text>
                        </div>
                        <div style={{ marginBottom: '4px' }}>
                          <Text style={{ fontSize: '13px' }}>
                            {getChangeDescription(log)}
                          </Text>
                        </div>
                        {log.updateby && (
                          <Text type="secondary" style={{ fontSize: '12px' }}>
                            Changed by: {log.updateby}
                          </Text>
                        )}
                        {log.originaldate && log.newdate && log.originaldate !== log.newdate && (
                          <div style={{ marginTop: '4px', fontSize: '12px' }}>
                            <Text type="secondary">Date: </Text>
                            <Text delete>{formatDate(log.originaldate)}</Text>
                            <Text type="secondary"> → </Text>
                            <Text mark>{formatDate(log.newdate)}</Text>
                          </div>
                        )}
                        {log.originalstarttime && log.newstarttime && log.originalstarttime !== log.newstarttime && (
                          <div style={{ marginTop: '2px', fontSize: '12px' }}>
                            <Text type="secondary">Time: </Text>
                            <Text delete>{formatTime(log.originalstarttime)} - {formatTime(log.originalendtime)}</Text>
                            <Text type="secondary"> → </Text>
                            <Text mark>{formatTime(log.newstarttime)} - {formatTime(log.newendtime)}</Text>
                          </div>
                        )}
                      </div>
                    ),
                  }))}
                />
              ) : (
                <Empty 
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description="No change history available"
                  style={{ margin: '20px 0' }}
                />
              );
            })()}
          </Card>
        </div>
        </Form>
        </Spin>
      ) : (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <Empty description="No class details available" />
        </div>
      )}
    </Modal>
  );
};

export default ClassDetailsModal;